import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  CheckCircle2, Clock, BookOpen, Download, Lock, PlayCircle, Loader2,
  MapPin, ExternalLink, AlertTriangle, Phone,
} from "lucide-react";

const CATEGORY_STYLES = {
  separate_election: { color: "bg-[#FEF3C7] text-[#92600A]", icon: AlertTriangle },
  no_recognition: { color: "bg-[#FEE2E2] text-[#991B1B]", icon: AlertTriangle },
  entity_level_tax: { color: "bg-[#DBEAFE] text-[#1E40AF]", icon: AlertTriangle },
  recently_changed: { color: "bg-[#EDE9FE] text-[#5B21B6]", icon: AlertTriangle },
  follows_federal: { color: "bg-[#DCFCE7] text-[#166534]", icon: CheckCircle2 },
};

export default function Guide() {
  const navigate = useNavigate();
  const [steps, setSteps] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [hasAccess, setHasAccess] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [states, setStates] = useState([]);
  const [federalLinks, setFederalLinks] = useState([]);
  const [stateDetail, setStateDetail] = useState(null);

  useEffect(() => {
    api.get("/guide").then(({ data }) => setSteps(data)).catch(() => {});
    api.get("/progress").then(({ data }) => setCompleted(data.completed_steps)).catch(() => {});
    api.get("/guide/access").then(({ data }) => setHasAccess(data.has_access)).catch(() => {});
    api.get("/states").then(({ data }) => {
      setStates(data.states);
      setFederalLinks(data.federal_links);
    }).catch(() => {});
  }, []);

  const selectState = async (code) => {
    try {
      const { data } = await api.get(`/states/${code}`);
      setStateDetail(data);
    } catch {
      /* ignore */
    }
  };

  const toggle = async (stepId) => {
    try {
      const { data } = await api.post("/progress/toggle", { step_id: stepId });
      setCompleted(data.completed_steps);
    } catch {
      /* ignore */
    }
  };

  const downloadGuide = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/guide/download", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ScorpPath-LLC-to-SCorp-Guide.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the guide. Make sure your plan is active.");
    } finally {
      setDownloading(false);
    }
  };

  const pct = steps.length ? Math.round((completed.length / steps.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-[#F9F9F8] font-body">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 sm:px-12 pt-28 pb-20">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#114A31] flex items-center gap-2">
          <BookOpen className="h-3.5 w-3.5" /> Self-Guided Path
        </span>
        <h1 className="mt-3 font-heading text-3xl lg:text-4xl tracking-tight font-medium text-[#1C1C1C]">
          Your LLC → S-Corp conversion roadmap
        </h1>
        <p className="mt-3 text-[#595D5A]">
          Work through each step at your own pace. Check items off as you complete them — your progress saves automatically.
        </p>

        <div className="mt-6 bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="progress-card">
          <div className="flex items-center justify-between">
            <span className="font-heading font-semibold text-[#1C1C1C]">Overall progress</span>
            <span className="font-heading font-bold text-[#114A31]" data-testid="progress-percent">{pct}%</span>
          </div>
          <Progress value={pct} className="mt-3 h-2 bg-[#E6F0EB]" />
          <p className="mt-2 text-sm text-[#595D5A]">
            {completed.length} of {steps.length} steps complete
          </p>
        </div>

        {/* Downloadable comprehensive guide */}
        <div className="mt-6 rounded-2xl p-6 bg-[#114A31] text-white flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="guide-download-card">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
              <BookOpen className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-heading font-semibold">The Complete LLC → S-Corp Guide (PDF)</h3>
              <p className="text-sm text-white/70 mt-0.5">
                A comprehensive, plain-English how-to walkthrough you can download and keep.
              </p>
            </div>
          </div>
          {hasAccess ? (
            <Button
              onClick={downloadGuide}
              disabled={downloading}
              data-testid="download-guide-btn"
              className="rounded-full bg-white text-[#114A31] hover:bg-[#E6F0EB] px-6 shrink-0"
            >
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-2" /> Download PDF</>}
            </Button>
          ) : (
            <Button
              onClick={() => navigate("/#pricing")}
              data-testid="unlock-guide-btn"
              className="rounded-full bg-white text-[#114A31] hover:bg-[#E6F0EB] px-6 shrink-0"
            >
              <Lock className="h-4 w-4 mr-2" /> Unlock with a plan
            </Button>
          )}
        </div>

        {/* State-specific requirements */}
        <div className="mt-6 bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="state-section">
          <div className="flex items-center gap-2 text-[#114A31]">
            <MapPin className="h-5 w-5" />
            <span className="font-heading font-semibold">State-specific requirements</span>
          </div>
          <p className="mt-1 text-sm text-[#595D5A]">
            The Form 2553 election is federal and applies in every state. A few states don't simply follow it —
            pick your state to see what's different and where to verify it. These are official links to research
            further, not tax advice.
          </p>

          <div className="mt-4 max-w-xs">
            <Select onValueChange={selectState}>
              <SelectTrigger className="rounded-xl border-[#E5E5E5] bg-white" data-testid="state-select">
                <SelectValue placeholder="Select your state…" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {states.map((s) => (
                  <SelectItem key={s.code} value={s.code} data-testid={`state-option-${s.code}`}>
                    {s.name}{s.flagged ? "  ⚠️" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {stateDetail && (
            <div className="mt-5 rounded-xl border border-[#E5E5E5] p-5 bg-[#F9F9F8]" data-testid="state-detail">
              {(() => {
                const cs = CATEGORY_STYLES[stateDetail.category] || CATEGORY_STYLES.follows_federal;
                const Icon = cs.icon;
                return (
                  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full ${cs.color}`}>
                    <Icon className="h-3.5 w-3.5" /> {stateDetail.category_label}
                  </span>
                );
              })()}
              <h4 className="mt-3 font-heading font-semibold text-[#1C1C1C]">{stateDetail.headline}</h4>
              <ul className="mt-3 space-y-2">
                {stateDetail.details.map((d, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#1C1C1C]">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#114A31] shrink-0" />
                    {d}
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex flex-wrap gap-2">
                {stateDetail.links.map((l, i) => (
                  <a
                    key={i}
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`state-link-${i}`}
                    className="inline-flex items-center gap-1.5 text-xs font-medium bg-white border border-[#E5E5E5] hover:border-[#114A31] rounded-lg px-3 py-2 text-[#114A31] transition-colors"
                  >
                    {l.label} <ExternalLink className="h-3 w-3" />
                  </a>
                ))}
              </div>

              {stateDetail.payroll && (
                <div className="mt-4 pt-4 border-t border-[#E5E5E5]" data-testid="state-payroll">
                  <p className="text-xs font-bold tracking-wide uppercase text-[#595D5A] mb-2">
                    Payroll registration
                  </p>
                  {stateDetail.payroll.no_income_tax && (
                    <p className="text-xs text-[#166534] bg-[#DCFCE7] inline-block px-2.5 py-1 rounded-full mb-2">
                      No state income-tax withholding — register SUI only
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {stateDetail.payroll.withholding && (
                      <a
                        href={stateDetail.payroll.withholding.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="payroll-withholding-link"
                        className="inline-flex items-center gap-1.5 text-xs font-medium bg-white border border-[#E5E5E5] hover:border-[#114A31] rounded-lg px-3 py-2 text-[#114A31] transition-colors"
                      >
                        Withholding: {stateDetail.payroll.withholding.label} <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                    {stateDetail.payroll.sui && (
                      <a
                        href={stateDetail.payroll.sui.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="payroll-sui-link"
                        className="inline-flex items-center gap-1.5 text-xs font-medium bg-white border border-[#E5E5E5] hover:border-[#114A31] rounded-lg px-3 py-2 text-[#114A31] transition-colors"
                      >
                        Unemployment: {stateDetail.payroll.sui.label} <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
              )}
              {stateDetail.flagged && (
                <div className="mt-4 pt-4 border-t border-[#E5E5E5] flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <p className="text-sm text-[#595D5A]">Want us to handle the state-specific filing for you?</p>
                  <Button
                    onClick={() => navigate("/#pricing")}
                    data-testid="state-call-btn"
                    className="rounded-full bg-[#114A31] hover:bg-[#1A6B46] text-white"
                  >
                    <Phone className="h-4 w-4 mr-2" /> Schedule a call
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Official federal resources */}
        {federalLinks.length > 0 && (
          <div className="mt-6 bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="federal-links">
            <div className="flex items-center gap-2 text-[#114A31]">
              <ExternalLink className="h-5 w-5" />
              <span className="font-heading font-semibold">Official resources</span>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {federalLinks.map((l, i) => (
                <a
                  key={i}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`federal-link-${i}`}
                  className="inline-flex items-center gap-1.5 text-xs font-medium bg-[#F9F9F8] border border-[#E5E5E5] hover:border-[#114A31] rounded-lg px-3 py-2 text-[#114A31] transition-colors"
                >
                  {l.label} <ExternalLink className="h-3 w-3" />
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 space-y-4" data-testid="guide-steps">
          {steps.map((step, i) => {
            const done = completed.includes(step.id);
            return (
              <div
                key={step.id}
                data-testid={`guide-step-${step.id}`}
                className={`rounded-2xl border p-5 transition-all ${
                  done ? "border-[#114A31]/30 bg-[#E6F0EB]/40" : "border-[#E5E5E5] bg-white"
                }`}
              >
                <div className="flex items-start gap-4">
                  <Checkbox
                    checked={done}
                    onCheckedChange={() => toggle(step.id)}
                    data-testid={`step-checkbox-${step.id}`}
                    className="mt-1 h-5 w-5 rounded-md border-[#114A31] data-[state=checked]:bg-[#114A31]"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-xs font-mono text-[#595D5A]">Step {i + 1}</span>
                      <span className="flex items-center gap-1 text-xs text-[#595D5A]">
                        <Clock className="h-3 w-3" /> {step.duration}
                      </span>
                      {done && (
                        <span className="flex items-center gap-1 text-xs text-[#114A31] font-medium">
                          <CheckCircle2 className="h-3.5 w-3.5" /> Done
                        </span>
                      )}
                    </div>
                    <h3 className={`mt-1 font-heading text-lg font-semibold ${done ? "text-[#114A31]" : "text-[#1C1C1C]"}`}>
                      {step.title}
                    </h3>
                    <p className="text-sm text-[#595D5A] mt-1">{step.summary}</p>

                    <Accordion type="single" collapsible className="mt-2">
                      <AccordionItem value="details" className="border-none">
                        <AccordionTrigger className="text-sm text-[#114A31] hover:no-underline py-2" data-testid={`step-details-${step.id}`}>
                          View details
                        </AccordionTrigger>
                        <AccordionContent>
                          <ul className="space-y-2 pl-1">
                            {step.details.map((d, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-[#1C1C1C]">
                                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#114A31] shrink-0" />
                                {d}
                              </li>
                            ))}
                          </ul>
                          {step.video_url ? (
                            <div className="mt-4 aspect-video rounded-xl overflow-hidden border border-[#E5E5E5]">
                              <iframe
                                src={step.video_url}
                                title={`${step.title} walkthrough`}
                                className="w-full h-full"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                data-testid={`step-video-${step.id}`}
                              />
                            </div>
                          ) : (
                            <div className="mt-4 flex items-center gap-2 text-sm text-[#595D5A] bg-[#F9F9F8] rounded-xl px-4 py-3">
                              <PlayCircle className="h-4 w-4 text-[#114A31]" />
                              Video walkthrough coming soon for this step.
                            </div>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
